import { useEffect, useState } from 'react';
import axios from 'axios';
import { NewspaperClipping, ArrowClockwise, Sparkle, Robot, CheckCircle, Warning, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/AuthContext';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  ja: {
    title: '更新履歴',
    subtitle: 'Sensbey Web App のリリース履歴。最新の変更点や追加機能を確認できます。',
    empty: 'まだ公開された更新履歴はありません。',
    loading: '更新履歴を読み込み中…',
    langLabel: '表示言語',
    fetchError: '更新履歴の取得に失敗しました',
  },
  en: {
    title: 'Changelog',
    subtitle: 'Release history for Sensbey Web App — see what changed in each update.',
    empty: 'No changelog entries have been published yet.',
    loading: 'Loading changelog…',
    langLabel: 'Language',
    fetchError: 'Failed to load changelog',
  },
};

/**
 * Timeline changelog viewer. Reuses the curated bilingual CHANGELOG array
 * from the backend `changelog_bot.py` so admins see the same content that's
 * posted to the "お知らせ" bot channel — no drift, no duplication.
 */
export default function ChangelogPage() {
  const { user } = useAuth();
  // Default to user's locale (ja by default, matches app default)
  const initialLang = (user?.language === 'en') ? 'en' : 'ja';
  const [lang, setLang] = useState(initialLang);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = T[lang];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/changelog`, { withCredentials: true });
        if (!cancelled) setEntries(r.data.entries || []);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || t.fetchError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t.fetchError]);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="changelog-page">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-400">Sensbey Web App</p>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mt-1 flex items-center gap-2">
          <NewspaperClipping size={26} weight="duotone" className="text-teal-700" />
          {t.title}
        </h1>
        <p className="text-xs sm:text-sm text-zinc-500 mt-1 leading-relaxed">
          {t.subtitle}
        </p>

        {/* Language pill toggle */}
        <div className="mt-3 inline-flex items-center gap-1 p-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-[11px]" data-testid="changelog-lang-toggle">
          <button
            type="button"
            onClick={() => setLang('ja')}
            className={`px-3 py-1 rounded-full font-semibold transition-all ${
              lang === 'ja' ? 'bg-white shadow text-teal-700' : 'text-zinc-500 hover:text-zinc-800'
            }`}
            data-testid="changelog-lang-ja"
          >日本語</button>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={`px-3 py-1 rounded-full font-semibold transition-all ${
              lang === 'en' ? 'bg-white shadow text-teal-700' : 'text-zinc-500 hover:text-zinc-800'
            }`}
            data-testid="changelog-lang-en"
          >English</button>
        </div>
      </header>

      {loading ? (
        <div className="rounded-xl border border-zinc-200 bg-white shadow-sm px-5 py-8 flex items-center justify-center gap-2 text-zinc-500 text-[13px]" data-testid="changelog-loading">
          <ArrowClockwise size={16} weight="duotone" className="animate-spin" />
          {t.loading}
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-xl border border-zinc-200 bg-white shadow-sm px-5 py-8 text-center text-zinc-500 text-[13px]" data-testid="changelog-empty">
          {t.empty}
        </div>
      ) : (
        <ol className="relative border-l-2 border-indigo-100 pl-6 ml-2 space-y-6" data-testid="changelog-timeline">
          {entries.map((entry, idx) => (
            <ChangelogEntry key={entry.version} entry={entry} lang={lang} isNewest={idx === 0} />
          ))}
        </ol>
      )}

      {/* Admin-only bot sync panel */}
      {user?.role === 'admin' && <BotSyncPanel />}
    </div>
  );
}


/**
 * Admin-only footer panel that lets admins see the bot's auto-announce
 * state (which versions have been posted to お知らせ) and manually
 * trigger a re-sync — normally the bot runs on backend startup, so this
 * is the escape hatch for adding a fresh entry between deploys.
 */
function BotSyncPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/settings/changelog/sync-status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || '同期状態の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/settings/changelog/sync`, {}, { withCredentials: true });
      const n = r.data.newly_posted_count || 0;
      if (n === 0) {
        toast.success('すべて同期済みです（新規投稿なし）');
      } else {
        toast.success(`${n} 件の新しい更新を「お知らせ」に投稿しました`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || '同期に失敗しました');
    } finally {
      setSyncing(false);
    }
  };

  if (loading || !status) return null;

  const hasPending = status.pending_count > 0;

  return (
    <div
      className={`rounded-xl border-2 ${hasPending ? 'border-amber-300 bg-amber-50' : 'border-zinc-200 bg-zinc-50'} shadow-sm overflow-hidden`}
      data-testid="changelog-bot-sync-panel"
    >
      <div className="px-5 py-4 flex items-start gap-3">
        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${
          hasPending ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
        }`}>
          <Robot size={20} weight="duotone" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-zinc-900 text-[14px] flex items-center gap-2">
            Bot 自動同期
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${
                hasPending
                  ? 'bg-white border-amber-300 text-amber-800'
                  : 'bg-white border-emerald-300 text-emerald-800'
              }`}
              data-testid="changelog-sync-badge"
            >
              {hasPending ? (
                <><Warning size={9} weight="fill" />未投稿 {status.pending_count}</>
              ) : (
                <><CheckCircle size={9} weight="fill" />同期済み</>
              )}
            </span>
          </h3>
          <p className="text-[12px] text-zinc-600 mt-1 leading-relaxed">
            この更新履歴はバックエンド起動時に自動で「📢 お知らせ」チャンネルに投稿されます。
            <span className="text-zinc-500"> 万一投稿されていないエントリがある場合は下の「今すぐ同期」を押してください。</span>
          </p>

          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
            <MiniStat label="エントリ総数" value={status.total_entries} testid="changelog-sync-total" />
            <MiniStat label="投稿済み" value={status.posted_count} accent="emerald" testid="changelog-sync-posted" />
            <MiniStat
              label="未投稿"
              value={status.pending_count}
              accent={hasPending ? 'amber' : 'zinc'}
              testid="changelog-sync-pending"
            />
          </div>

          {hasPending && (
            <div className="mt-2 text-[11px] text-amber-800" data-testid="changelog-sync-pending-list">
              未投稿: {status.pending_versions.map((v) => (
                <span key={v} className="font-mono bg-white/70 border border-amber-200 rounded px-1 py-0.5 mr-1">v{v}</span>
              ))}
            </div>
          )}

          {status.last_synced_at && (
            <div className="mt-2 text-[10px] text-zinc-500" data-testid="changelog-sync-last-synced">
              最終同期: <span className="font-mono">{formatDateTime(status.last_synced_at)}</span>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <Button
              onClick={triggerSync}
              disabled={syncing}
              size="sm"
              data-testid="changelog-sync-run"
              className={hasPending
                ? 'bg-amber-600 hover:bg-amber-700 text-white h-8 text-[11px]'
                : 'bg-zinc-700 hover:bg-zinc-800 text-white h-8 text-[11px]'}
            >
              {syncing ? (
                <><ArrowClockwise size={11} className="mr-1 animate-spin" />同期中…</>
              ) : (
                <><ArrowsClockwise size={11} className="mr-1" />今すぐ同期</>
              )}
            </Button>
            {hasPending && (
              <span className="text-[10px] text-amber-700">
                {status.pending_count} 件の未投稿エントリがあります
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function MiniStat({ label, value, accent = 'zinc', testid }) {
  const map = {
    zinc: 'bg-white border-zinc-200 text-zinc-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-100 border-amber-300 text-amber-900',
  };
  return (
    <div className={`rounded border px-2 py-1 ${map[accent]}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="font-bold tabular-nums text-[13px] leading-none mt-0.5">{value}</div>
    </div>
  );
}


function ChangelogEntry({ entry, lang, isNewest }) {
  const title = lang === 'en' ? entry.title_en : entry.title_ja;
  const bullets = lang === 'en' ? entry.en : entry.ja;

  return (
    <li className="relative" data-testid={`changelog-entry-${entry.version}`}>
      {/* Timeline dot */}
      <span
        className={`absolute -left-[34px] top-1 w-4 h-4 rounded-full ring-4 ring-white flex items-center justify-center ${
          isNewest ? 'bg-teal-700' : 'bg-indigo-300'
        }`}
      >
        {isNewest && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
      </span>

      <div className={`rounded-xl border shadow-sm bg-white overflow-hidden ${
        isNewest ? 'border-indigo-300' : 'border-zinc-200'
      }`}>
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[11px] font-bold px-2 py-0.5 rounded bg-indigo-100 text-indigo-800" data-testid={`changelog-version-${entry.version}`}>
            v{entry.version}
          </span>
          {isNewest && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-teal-700 text-white">
              <Sparkle size={10} weight="fill" />
              {lang === 'en' ? 'LATEST' : '最新'}
            </span>
          )}
          <h2 className="font-bold text-zinc-900 text-[14px] leading-snug flex-1 min-w-0">
            {title}
          </h2>
        </div>
        <ul className="px-5 py-3 space-y-1.5 text-[13px] text-zinc-700 leading-relaxed">
          {(bullets || []).map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-indigo-500 mt-1 shrink-0">·</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}
